import { fetchWithRetry, responseError } from './apiError';
import type { WebSearchActivity } from './chatService';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/api/v1/assistant`;

export interface AssistantConversation {
  id: string;
  title: string | null;
  model: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

export type AttachmentKind = 'text' | 'table' | 'pdf' | 'doc' | 'sheet' | 'image' | 'code';

export interface AssistantAttachment {
  id: string;
  message_id: string | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  kind: AttachmentKind;
  created_at: string;
}

export interface AssistantArtifact {
  id: string;
  message_id: string | null;
  filename: string;
  content_type: string;
  size_bytes: number;
  url: string;
  created_at?: string;
}

/** What a finished `web_search` call found and cost (absent for `run_python`). */
export type ToolSearchResult = Pick<WebSearchActivity, 'status' | 'sources' | 'credits'>;

export interface ToolTraceEntry extends Partial<ToolSearchResult> {
  name: string;
  input_preview: string;
  ok: boolean;
  stdout_preview: string;
  stderr_preview?: string;
  artifact_ids?: string[];
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  model: string | null;
  status: 'complete' | 'stopped' | 'failed';
  created_at: string;
  tool_trace?: ToolTraceEntry[] | null;
  attachments?: AssistantAttachment[];
  artifacts?: AssistantArtifact[];
}

export interface AssistantConversationDetail extends AssistantConversation {
  messages: AssistantMessage[];
  summary?: string | null;
  compacted_through_message_id?: string | null;
}

export interface CompactionResult {
  summarized_messages: number;
  summary: string | null;
  compacted_through_message_id: string | null;
}

export interface MapReduceProgress {
  phase: 'map' | 'reduce';
  done: number;
  total: number;
}

export interface UserMemory {
  id: string;
  content: string;
  category: 'profile' | 'preference' | 'project' | 'fact';
  source_conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

export type StreamEventType =
  | 'user_message' | 'token' | 'done' | 'reflection' | 'error'
  | 'tool_call' | 'tool_result' | 'compaction' | 'mapreduce';

export interface StreamEvent {
  type: StreamEventType;
  message_id?: string;
  assistant_message_id?: string;
  conversation_id?: string;
  content?: string;
  title?: string | null;
  model?: string;
  memories_added?: number;
  message?: string;
  artifacts?: AssistantArtifact[];
  /** The tool call's id: parallel calls of one turn each settle their own row. */
  id?: string;
  name?: string;
  input_preview?: string;
  ok?: boolean;
  stdout_preview?: string;
  status?: ToolSearchResult['status'];
  sources?: ToolSearchResult['sources'];
  credits?: number;
  summarized_messages?: number;
  compacted_through_message_id?: string | null;
  phase?: MapReduceProgress['phase'];
  done?: number;
  total?: number;
}

export interface ReplyStreamCallbacks {
  onUserMessage?: (messageId: string) => void;
  onToken: (text: string) => void;
  onDone: (assistantMessageId: string, title: string | null, artifacts: AssistantArtifact[]) => void;
  onReflection?: (title: string | null, memoriesAdded: number) => void;
  onToolCall?: (name: string, inputPreview: string, id?: string) => void;
  onToolResult?: (
    ok: boolean, stdoutPreview: string, artifacts: AssistantArtifact[], search?: ToolSearchResult, id?: string,
  ) => void;
  onCompaction?: (summarizedMessages: number, compactedThroughMessageId: string | null) => void;
  onMapReduce?: (progress: MapReduceProgress) => void;
  onError: (message: string) => void;
}

const authHeaders = (): Record<string, string> => ({ Authorization: `Bearer ${tokenService.getAccessToken()}` });

const jsonHeaders = (): Record<string, string> => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const okJson = async <T>(response: globalThis.Response): Promise<T> => {
  if (!response.ok) throw await responseError(response);

  return response.json() as Promise<T>;
};

const okEmpty = async (response: globalThis.Response): Promise<void> => {
  if (!response.ok) throw await responseError(response);
};

/** Split an SSE buffer into complete events; the tail (a partial event) is returned for the next chunk. */
export const parseSseBuffer = (buffer: string): { events: StreamEvent[]; remaining: string } => {
  const parts = buffer.split('\n\n');
  const remaining = parts.pop() ?? '';
  const events: StreamEvent[] = [];

  parts.forEach((part) => {
    const line = part.split('\n').find((candidate) => candidate.startsWith('data: '));

    if (!line) return;
    try {
      events.push(JSON.parse(line.slice(6)) as StreamEvent);
    } catch {
      events.push({ type: 'error', message: 'The reply could not be read.' });
    }
  });

  return { events, remaining };
};

type ProgressHandler = (event: StreamEvent, callbacks: ReplyStreamCallbacks) => void;

/** Events emitted while the model works (tools, compaction, corpus pass) before the answer streams. */
const progressHandlers: Partial<Record<StreamEventType, ProgressHandler>> = {
  tool_call: (event, callbacks) => (
    callbacks.onToolCall?.(event.name ?? 'run_python', event.input_preview ?? '', event.id)
  ),
  tool_result: (event, callbacks) => callbacks.onToolResult?.(
    Boolean(event.ok),
    event.stdout_preview ?? '',
    event.artifacts ?? [],
    event.sources ? { status: event.status ?? 'done', sources: event.sources, credits: event.credits ?? 0 } : undefined,
    event.id,
  ),
  compaction: (event, callbacks) => (
    callbacks.onCompaction?.(event.summarized_messages ?? 0, event.compacted_through_message_id ?? null)
  ),
  mapreduce: (event, callbacks) => (
    callbacks.onMapReduce?.({ phase: event.phase ?? 'map', done: event.done ?? 0, total: event.total ?? 0 })
  ),
};

const dispatchProgressEvent = (event: StreamEvent, callbacks: ReplyStreamCallbacks): boolean => {
  const handler = progressHandlers[event.type];

  if (!handler) return false;
  handler(event, callbacks);

  return true;
};

export const dispatchStreamEvent = (event: StreamEvent, callbacks: ReplyStreamCallbacks): void => {
  if (dispatchProgressEvent(event, callbacks)) return;
  switch (event.type) {
  case 'user_message':
    callbacks.onUserMessage?.(event.message_id ?? '');
    break;
  case 'token':
    callbacks.onToken(event.content ?? '');
    break;
  case 'done':
    callbacks.onDone(event.assistant_message_id ?? '', event.title ?? null, event.artifacts ?? []);
    break;
  case 'reflection':
    callbacks.onReflection?.(event.title ?? null, event.memories_added ?? 0);
    break;
  default:
    callbacks.onError(event.message || 'The reply stopped unexpectedly. Try again.');
  }
};

const readReplyStream = async (
  body: globalThis.ReadableStream<Uint8Array>,
  callbacks: ReplyStreamCallbacks,
): Promise<void> => {
  const reader = body.getReader();
  const decoder = new window.TextDecoder();
  let buffer = '';

  for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
    const parsed = parseSseBuffer(buffer + decoder.decode(chunk.value, { stream: true }));

    buffer = parsed.remaining;
    parsed.events.forEach((event) => dispatchStreamEvent(event, callbacks));
  }
};

const listConversations = (): Promise<AssistantConversation[]> => (
  fetchWithRetry(`${BASE}/conversations`, { headers: authHeaders() }).then((r) => okJson<AssistantConversation[]>(r))
);

const createConversation = (title?: string): Promise<AssistantConversationDetail> => (
  fetchWithRetry(`${BASE}/conversations`, {
    method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ title: title ?? null }),
  }).then((r) => okJson<AssistantConversationDetail>(r))
);

const getConversation = (conversationId: string): Promise<AssistantConversationDetail> => (
  fetchWithRetry(`${BASE}/conversations/${encodeURIComponent(conversationId)}`, { headers: authHeaders() })
    .then((r) => okJson<AssistantConversationDetail>(r))
);

const renameConversation = (conversationId: string, title: string): Promise<AssistantConversation> => (
  fetchWithRetry(`${BASE}/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ title }),
  }).then((r) => okJson<AssistantConversation>(r))
);

const deleteConversation = (conversationId: string): Promise<void> => (
  fetchWithRetry(`${BASE}/conversations/${encodeURIComponent(conversationId)}`, {
    method: 'DELETE', headers: authHeaders(),
  }).then(okEmpty)
);

/** Send one user turn (optionally referencing uploaded attachments) and stream the reply. */
const streamReply = async (
  conversationId: string,
  content: string,
  callbacks: ReplyStreamCallbacks,
  signal?: globalThis.AbortSignal,
  model?: string,
  attachmentIds: string[] = [],
): Promise<void> => {
  const body = JSON.stringify({ content, model: model ?? null, attachment_ids: attachmentIds });
  const response = await fetch(`${BASE}/conversations/${encodeURIComponent(conversationId)}/messages/stream`, {
    method: 'POST', headers: jsonHeaders(), body, signal,
  });

  if (!response.ok || !response.body) throw await responseError(response);

  await readReplyStream(response.body, callbacks);
};

const conversationPath = (conversationId: string): string => `${BASE}/conversations/${encodeURIComponent(conversationId)}`;

const attachmentForm = (files: globalThis.File[]): globalThis.FormData => {
  const form = new window.FormData();

  files.forEach((file) => form.append('files', file, file.name));

  return form;
};

const uploadOutcome = (
  request: globalThis.XMLHttpRequest,
  resolve: (rows: AssistantAttachment[]) => void,
  reject: (error: Error) => void,
) => () => {
  if (request.status >= 200 && request.status < 300) {
    resolve(JSON.parse(request.responseText) as AssistantAttachment[]);
  } else {
    reject(new Error(uploadFailureMessage(request.responseText)));
  }
};

/** Upload files with progress (XHR, since fetch cannot report upload progress). */
const uploadAttachments = (
  conversationId: string,
  files: globalThis.File[],
  onProgress?: (fraction: number) => void,
): Promise<AssistantAttachment[]> => new Promise((resolve, reject) => {
  const request = new window.XMLHttpRequest();

  request.open('POST', `${conversationPath(conversationId)}/attachments`);
  request.setRequestHeader('Authorization', `Bearer ${tokenService.getAccessToken()}`);
  request.upload.onprogress = (event) => { if (event.lengthComputable) onProgress?.(event.loaded / event.total); };
  request.onerror = () => reject(new Error('The upload failed. Check your connection and try again.'));
  request.onload = uploadOutcome(request, resolve, reject);
  request.send(attachmentForm(files));
});

const uploadFailureMessage = (responseText: string): string => {
  try {
    const detail = (JSON.parse(responseText) as { detail?: unknown }).detail;

    if (typeof detail === 'string') return detail;
  } catch {
    // fall through to the generic message
  }

  return 'The files could not be uploaded.';
};

const listAttachments = (conversationId: string): Promise<AssistantAttachment[]> => (
  fetchWithRetry(`${conversationPath(conversationId)}/attachments`, { headers: authHeaders() })
    .then((r) => okJson<AssistantAttachment[]>(r))
);

const deleteAttachment = (conversationId: string, attachmentId: string): Promise<void> => (
  fetchWithRetry(`${conversationPath(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`, {
    method: 'DELETE', headers: authHeaders(),
  }).then(okEmpty)
);

const listArtifacts = (conversationId: string): Promise<AssistantArtifact[]> => (
  fetchWithRetry(`${conversationPath(conversationId)}/artifacts`, { headers: authHeaders() })
    .then((r) => okJson<AssistantArtifact[]>(r))
);

/** Absolute download URL for an artifact (the API returns an API-relative path). */
export const artifactUrl = (artifact: Pick<AssistantArtifact, 'url'>): string => (
  artifact.url.startsWith('/') ? `${API_URL}${artifact.url}` : artifact.url
);

/** Fetch an artifact's bytes with the auth header; callers turn the blob into an object URL. */
const fetchArtifactBlob = async (artifact: Pick<AssistantArtifact, 'url'>): Promise<globalThis.Blob> => {
  const response = await fetchWithRetry(artifactUrl(artifact), { headers: authHeaders() });

  if (!response.ok) throw await responseError(response);

  return response.blob();
};

const compactConversation = (conversationId: string): Promise<CompactionResult> => (
  fetchWithRetry(`${conversationPath(conversationId)}/compact`, { method: 'POST', headers: authHeaders() })
    .then((r) => okJson<CompactionResult>(r))
);

const listMemories = (): Promise<UserMemory[]> => (
  fetchWithRetry(`${BASE}/memories`, { headers: authHeaders() }).then((r) => okJson<UserMemory[]>(r))
);

const addMemory = (content: string, category: UserMemory['category'] = 'fact'): Promise<UserMemory> => (
  fetchWithRetry(`${BASE}/memories`, {
    method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ content, category }),
  }).then((r) => okJson<UserMemory>(r))
);

const updateMemory = (memoryId: string, content: string): Promise<UserMemory> => (
  fetchWithRetry(`${BASE}/memories/${encodeURIComponent(memoryId)}`, {
    method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ content }),
  }).then((r) => okJson<UserMemory>(r))
);

const deleteMemory = (memoryId: string): Promise<void> => (
  fetchWithRetry(`${BASE}/memories/${encodeURIComponent(memoryId)}`, { method: 'DELETE', headers: authHeaders() })
    .then(okEmpty)
);

const clearMemories = (): Promise<void> => (
  fetchWithRetry(`${BASE}/memories`, { method: 'DELETE', headers: authHeaders() }).then(okEmpty)
);

export const assistantChatService = {
  listConversations,
  createConversation,
  getConversation,
  renameConversation,
  deleteConversation,
  streamReply,
  uploadAttachments,
  listAttachments,
  deleteAttachment,
  listArtifacts,
  fetchArtifactBlob,
  compactConversation,
  listMemories,
  addMemory,
  updateMemory,
  deleteMemory,
  clearMemories,
};
